'use client'
import { useState, useEffect } from 'react'
import QRCode from 'qrcode.react'
import { Dialog } from '@headlessui/react'
import { useDispatch, useSelector } from 'react-redux'
import { createSessionId } from '@/redux/slices/imageSessionSlice'
import LogoMinified from '@/images/logo-minified.svg'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { SvgPhoneScanIcon } from '@/components/icons'
import { Tooltip } from 'react-tooltip'
import { usePathname } from 'next/navigation'
import { ImageSessionState } from '@/types/redux'
import isMobile from 'is-mobile'
import { LESSON_QR_CODE_CLICK } from '@/utils/mixpanel/eventNames'
import { trackEventMixpanel } from '@/utils/mixpanel'
import { MobileInput } from '@/components/page-partials/quiz/mobile-upload'
import { CameraIcon } from '@heroicons/react/20/solid'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  triggerBtnClassName?: string
  source?: string
}

export default function QRCodePopover({
  triggerBtnClassName = 'h-10 pl-2 flex items-center justify-center gap-2 font-semibold text-sm text-v8-violet',
  source,
  ...props
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [isMobileDevice, setIsMobileDevice] = useState(false)
  const [baseURL, setBaseURL] = useState('')

  const dispatch = useDispatch()
  const { error, loading, sessionId } = useSelector(
    ({ imageSession }: { imageSession: ImageSessionState }) => imageSession,
  )
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = window.location.origin
      setBaseURL(url)
      const isMobileDevice = isMobile({ tablet: false }) // Optionally include tablets
      setIsMobileDevice(isMobileDevice)
    }
  }, [])

  useEffect(() => {
    dispatch(createSessionId())
  }, [dispatch])

  useEffect(() => {
    if (loading || error) {
      setIsOpen(false)
    }
  }, [loading, error])

  const handleOpen = () => {
    setIsOpen(true)

    // track Mix Panel Event
    // No event on the demo lesson (home page).
    if (pathname === '/') return

    trackEventMixpanel(LESSON_QR_CODE_CLICK, { source })
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const mobileInputUrl = `${baseURL}/mobile-input/${sessionId}`

  return (
    <div id="qr-code-popover" {...props}>
      {isMobileDevice ? (
        <button
          onClick={handleOpen}
          className={triggerBtnClassName}
          data-tooltip-id="uploadTooltip"
          data-tooltip-content="Upload Image"
          data-tooltip-place="top"
          type="button"
        >
          <CameraIcon className="w-[18px] h-[18px]" />
        </button>
      ) : (
        <button
          onClick={handleOpen}
          className={triggerBtnClassName}
          data-tooltip-id="scanQrTooltip"
          data-tooltip-content="Scan your work"
          data-tooltip-place="top"
          type="button"
        >
          <SvgPhoneScanIcon className="w-[18px] h-[18px] fill-v8-violet" />
        </button>
      )}

      <Tooltip id="uploadTooltip" className="z-[105] max-sm:hidden" />
      <Tooltip id="scanQrTooltip" className="z-[105] max-sm:hidden" />

      <Dialog
        open={isOpen}
        onClose={handleClose}
        className="fixed z-[9999] inset-0"
      >
        {isMobileDevice ? (
          <div className="flex items-center justify-center w-screen h-screen bg-black bg-opacity-30">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30 z-[9999]" />
            <div className="relative bg-white w-full h-full z-[10000]">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-v8-mut hover:text-v8-ink z-10"
                type="button"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              {sessionId ? (
                <MobileInput id={sessionId} onClose={handleClose} />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-screen px-4">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30 z-[9999]" />
            <div className="relative bg-white rounded-lg max-w-sm mx-auto p-6 z-[10000]">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 text-v8-mut hover:text-v8-ink"
                type="button"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
              <Dialog.Title className="text-lg text-center font-bold text-v8-violet mb-4">
                Scan this QR Code
              </Dialog.Title>
              <QRCode
                value={mobileInputUrl}
                size={256}
                imageSettings={{
                  src: LogoMinified?.src,
                  height: 30,
                  width: 30,
                  excavate: true,
                }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </div>
  )
}
